import { useEffect, useRef } from 'react';
import {
  useConnectionStore,
  ConnectionStatus,
} from '../stores/connection.store';
import {
  shouldRunReconcileFetch,
  markReconcileFetchFired,
  RECONCILE_FETCH_THROTTLE_MS,
} from '../reconcile-throttle';
import { wsLogger } from '@/shared/lib/logger';

/**
 * L2 reconcile (per WEB-L2 in docs/REALTIME_RECONCILE_PLAN.md).
 *
 * Triggers `onReconnect` when the WebSocket transitions back into
 * `CONNECTED` from a non-cold-start state, throttled to one fire per
 * `RECONCILE_FETCH_THROTTLE_MS` window. The throttle absorbs rapid
 * SUSPENDED → CONNECTED → SUSPENDED → CONNECTED flaps so the reconcile
 * bundle (queue + balance + profile + invalidations) doesn't double-fire.
 *
 * A connect always passes through CONNECTING, so the first connect of a tab
 * fires too. That is wanted: a tab that just took leadership over from a
 * hidden leader missed every frame while nobody held a socket.
 */
export function useWebSocketReconnectSync(
  onReconnect: () => void | Promise<void>
) {
  const status = useConnectionStore((state) => state.status);
  const previousStatusRef = useRef<ConnectionStatus>(status);

  useEffect(() => {
    const transitionedIntoConnected =
      status === ConnectionStatus.CONNECTED &&
      previousStatusRef.current !== ConnectionStatus.CONNECTED &&
      previousStatusRef.current !== ConnectionStatus.DISCONNECTED;

    if (transitionedIntoConnected) {
      if (!shouldRunReconcileFetch()) {
        wsLogger.debug(
          `[L2_RECONCILE] reason=ws-reconnect throttled (window=${RECONCILE_FETCH_THROTTLE_MS}ms)`
        );
      } else {
        markReconcileFetchFired();
        wsLogger.info('[L2_RECONCILE] reason=ws-reconnect fired');

        const result = onReconnect();
        if (result instanceof Promise) {
          result.catch((error) => {
            wsLogger.error('[L2_RECONCILE] sync callback failed:', error);
          });
        }
      }
    }

    previousStatusRef.current = status;
  }, [status, onReconnect]);
}
